import { Box, Grid } from '@material-ui/core';
import classNames from 'classnames';
import isNil from 'lodash/isNil';
import React, { FC, useContext, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { compact } from 'lodash';
import { useSelector } from 'react-redux';
import {
  ConditionalWrapper,
  InlineTextButton,
  ListingImage,
  NamedLink,
  TypographyWrapper,
} from '..';
import { logSelectItem } from '../../analytics/pixelAnalytics';
import { ListingPageParamTab } from '../../containers/EditListingPage/EditListingPage.utils';
import {
  createPGListingDraftFromExistingListing,
  createSTListingDraftFromExistingListing,
  fetchAllDraftListingsForSourceListing,
} from '../../containers/ManagePurchasesPage/ManagePurchasesPage.duck';
import { useAppDispatch } from '../../hooks/appDispatch';
import { useShopConfig } from '../../hooks/shopConfig';
import { useBrandCountryConfig } from '../../hooks/useCountryConfig';
import { useEnabledCustomerExperiences } from '../../hooks/useEnabledCustomerExperiences';
import { useListingFlowConfig } from '../../hooks/useListingFlowConfig';
import { useRouteConfiguration } from '../../hooks/useRouteConfiguration';
import { useCurrentUserPermissions } from '../../hooks/useUserPermissions';
import { CoreBundleItem, Participant } from '../../types/apollo/generated/types.generated';
import { CheckoutLineItem } from '../../types/models/lineItem';
import { FindItemMethod, ListingItemType, ListingWithImages } from '../../types/sharetribe/listing';
import { getSharetribeAddressFromShippoAddress } from '../../util/address';
import { ITEM_AVAILABILITY_AVAILABLE } from '../../util/constants';
import { convertNumberToMoney, formatDollarAmountAsCurrency } from '../../util/currency';
import {
  createListingURLHelper,
  getShopifyProductVariantCompareAtAndPriceAverage,
} from '../../util/listings/listing';
import {
  BuildDraftListingPayload,
  buildPGDraftListingPayloadFromExistingListing,
  buildSTDraftListingPayloadFromExistingListing,
} from '../../util/listings/listingPayload';
import { createResourceLocatorString } from '../../util/routes';
import { createSlug, ListingPageParamType } from '../../util/urlHelpers';
import { useTradeInConfig } from '../../hooks/useTradeInConfig';
import IconRelistFilled from '../Icons/IconRelist/IconRelistFilled';
import { LISTING_STATE_DRAFT } from '../../util/types';
import { useCashPayoutPercentage } from '../../hooks/useCashPayoutPercentage';
import { useCreditPayoutPercentage } from '../../hooks/useCreditPayoutPercentage';
import CreditBoostChip from '../CreditBoostChip/CreditBoostChip';
import { DISPUTE_MODAL_SUMMARY_PAGE_REFERRER_LOCATION } from '../DisputeOrVerifyTradeInModal/ReturnSummary';
import { useIsMobile } from '../../hooks/useIsMobile';
import css from './ItemizedListing.module.css';
import { useListingConditionLabel } from '../../hooks/useListingConditionLabel';
import { getRecommendedPriceForCondition } from '../../util/listingPrice';
import AppContext from '../../context/AppContext';
import { Feature, isFeatureEnabled } from '../../util/featureFlags';

interface TradeInCreditInfoProps {
  creditPayoutFixedAmount?: number;
  creditPayoutPercentage?: number;
  disputeDescription?: string | null;
  hideDollarAmounts?: boolean;
}

export type RelistListingStub = {
  id: string;
  state: string;
  title: string;
};

const TradeInCreditInfo: FC<TradeInCreditInfoProps> = (props) => {
  const { creditPayoutFixedAmount, creditPayoutPercentage, disputeDescription, hideDollarAmounts } =
    props;

  const intl = useIntl();
  const { currencyConfig } = useBrandCountryConfig();

  const formattedCredit = !isNil(creditPayoutFixedAmount)
    ? intl.formatNumber(creditPayoutFixedAmount / 100, currencyConfig)
    : '';

  return (
    <>
      {!isNil(creditPayoutFixedAmount) && !hideDollarAmounts && (
        <h4>
          <TypographyWrapper variant="body1">
            Credit Earned:{' '}
            <TypographyWrapper
              variant="body1"
              typographyOverrides={{ display: 'inline' }}
              isDynamicValue
            >
              {formattedCredit}
            </TypographyWrapper>
          </TypographyWrapper>
        </h4>
      )}
      {!isNil(creditPayoutPercentage) && (
        <h4>
          <TypographyWrapper variant="body1">
            Credit Earned:{' '}
            <TypographyWrapper
              variant="body1"
              typographyOverrides={{ display: 'inline' }}
              isDynamicValue
            >
              {Math.floor(creditPayoutPercentage * 100).toString()}%
            </TypographyWrapper>
          </TypographyWrapper>
        </h4>
      )}
      {!isNil(disputeDescription) && (
        <h4>
          <TypographyWrapper variant="body1">
            Notes:{' '}
            <TypographyWrapper
              variant="body1"
              typographyOverrides={{ display: 'inline' }}
              isDynamicValue
            >
              {disputeDescription}
            </TypographyWrapper>
          </TypographyWrapper>
        </h4>
      )}
    </>
  );
};

interface ItemizedListingProps {
  listing: ListingWithImages;
  referrerForLogging: string;
  lineItem?: CheckoutLineItem;
  actionEl?: React.ReactElement;
  isDisputed?: boolean;
  isCanceled?: boolean;
  isVerified?: boolean;
  isReturned?: boolean;
  participant?: Participant;
  bundleItem?: CoreBundleItem;
}

const ItemizedListing: FC<ItemizedListingProps> = (props: ItemizedListingProps) => {
  const {
    listing,
    lineItem,
    actionEl,
    isDisputed = false,
    isCanceled = false,
    isVerified = false,
    isReturned = false,
    referrerForLogging,
    participant,
    bundleItem,
  } = props;

  const [resaleListing, setResaleListing] = useState<RelistListingStub | null>();
  const intl = useIntl();
  const isMobile = useIsMobile();
  const dispatch = useAppDispatch();
  const routes = useRouteConfiguration();
  const { currencyConfig } = useBrandCountryConfig();
  const {
    sizeVariantOptionName,
    additionalPayoutSettings,
    shopName,
    relistPurchasedListingActionText,
    resaleValueConfig,
    internationalConfig,
  } = useShopConfig();
  const tradeInConfig = useTradeInConfig();
  const { cashPayoutPercentageForTradeInResale } = tradeInConfig;
  const { conditionToRecommendedDiscount, pricingRestrictions } = useListingFlowConfig();
  const { isBrand, isTradeInAdmin, isTreet } = useCurrentUserPermissions();
  const { allowBuy, allowSell } = useEnabledCustomerExperiences();
  const cashPayoutPercentage = useCashPayoutPercentage();
  const creditPayoutPercentageConfig = useCreditPayoutPercentage(); // Only used to populate ReTreeted listing.
  const { treetId } = useContext(AppContext);
  const isListingFlowV2 = isFeatureEnabled(Feature.ListingFlowV2, treetId);
  const { shopId } = useSelector<any>((state) => state.initial) as any;
  const [draftListingsLoading, setDraftListingsLoading] = useState(false);

  useEffect(() => {
    const fetchListings = async () => {
      setDraftListingsLoading(true);
      const currentListingId = listing.id.uuid;
      const draftListings = await dispatch(fetchAllDraftListingsForSourceListing(currentListingId));
      const draftListing = compact(draftListings)[0];
      if (draftListing) {
        setResaleListing(draftListing);
      }
      setDraftListingsLoading(false);
    };

    fetchListings();
  }, []);

  const isPurchases = participant === Participant.Buyer;
  const isSales = participant === Participant.Seller;
  const {
    shopifyProductVariant,
    shopifyProductVariantSku,
    internalBrandNotes,
    payoutInfo,
    listingItemType,
    findItemMethod,
  } = listing.attributes.publicData || {};
  const { creditPayoutFixedAmount, creditPayoutPercentage, tradeInCreditPayoutBundleAmount } =
    payoutInfo || {};
  const disputeDescription = bundleItem?.dispute?.note;
  const size = listing.attributes.publicData?.[sizeVariantOptionName];

  const { conditionLabel } = useListingConditionLabel(listing);

  const isTradeInItem = listingItemType === ListingItemType.TradeIn;
  const isMarketplaceItem = listingItemType === ListingItemType.Marketplace;
  const wasCreditIssued =
    !!creditPayoutFixedAmount ||
    !!creditPayoutPercentage ||
    // tradeInCreditPayoutBundleAmount can be set to $0 for consigned items, e.g. for tenable6.
    !isNil(tradeInCreditPayoutBundleAmount);
  // Do not allow re-selling item that was listed as a non-Treet site brand
  const isListAnyBrandItem = findItemMethod === FindItemMethod.ListAnyBrand;
  const canRelistTradeInItem = isTradeInItem && isTradeInAdmin && isVerified && wasCreditIssued;
  const canRelistMarketplaceItem = isMarketplaceItem && allowSell && isVerified;
  const canCreateResaleDraftListing =
    isPurchases &&
    allowBuy &&
    !isListAnyBrandItem &&
    (canRelistTradeInItem || canRelistMarketplaceItem);
  const createDraftListingActionCopy = relistPurchasedListingActionText || 'Relist Item';
  const viewCreatedListingActionCopy = (createdListing?: RelistListingStub | null) => {
    if (draftListingsLoading) return 'Loading...';
    if (!createdListing) return createDraftListingActionCopy;
    // Normalize the state to accomodate both PG and Sharetribe states
    // eslint-disable-next-line no-nested-ternary
    return createdListing.state.toLowerCase() === LISTING_STATE_DRAFT.toLowerCase()
      ? isMobile
        ? 'Complete Draft'
        : 'Complete Draft Relisting'
      : 'View Relisted Item';
  };

  const category = listing?.attributes?.publicData?.category;

  // Dollar amts will be $0 if the brand is paying out a flat amount per trade-in bundle.
  const shouldHideLineItemTotals =
    isTradeInItem && !isNil(additionalPayoutSettings?.tradeInCreditPayoutBundleAmount);

  const isReturnSummaryPage = referrerForLogging === DISPUTE_MODAL_SUMMARY_PAGE_REFERRER_LOCATION;

  const getCreateListingDraftParams = (): BuildDraftListingPayload => {
    const relistedTradeInConditionToRecommendedDiscount =
      tradeInConfig?.relistedConditionToRecommendedDiscount;

    const { originalPrice } = listing.attributes.publicData;

    const shopifyVariantCompareAtPriceAndPriceAverage =
      getShopifyProductVariantCompareAtAndPriceAverage(shopifyProductVariant);

    const priceToUse =
      resaleValueConfig && shopifyVariantCompareAtPriceAndPriceAverage
        ? shopifyVariantCompareAtPriceAndPriceAverage
        : originalPrice;

    const recommendedPrice = getRecommendedPriceForCondition({
      price: convertNumberToMoney(priceToUse, currencyConfig.currency),
      condition: listing.attributes.publicData.condition,
      conditionToRecommendedDiscount:
        relistedTradeInConditionToRecommendedDiscount || conditionToRecommendedDiscount,
      pricingRestrictions,
      tags: listing.attributes.publicData.tags,
      category,
      resaleValueConfig,
    });

    const defaultParams = {
      listing,
      shopName,
      price: listing.attributes.price,
      availability: ITEM_AVAILABILITY_AVAILABLE,
      listingItemType: ListingItemType.Marketplace,
      cashPayoutPercentage,
      creditPayoutPercentage: creditPayoutPercentageConfig,
      isBrand: isBrand && !isTreet,
      sourceListingId: listing.id.uuid,
      findItemMethod: FindItemMethod.RelistFromTreet,
      internationalConfig,
    };
    const tradeInItemParams = {
      ...defaultParams,
      price: recommendedPrice
        ? convertNumberToMoney(recommendedPrice, currencyConfig.currency)
        : undefined,
      cashPayoutPercentage: cashPayoutPercentageForTradeInResale || cashPayoutPercentage,
      findItemMethod: FindItemMethod.TradeInUpload,
      // By default, assume relisted trade-in items will ship from the same location customers ship to.
      shipFromAddress: tradeInConfig?.tradeInShipToAddress
        ? getSharetribeAddressFromShippoAddress(tradeInConfig?.tradeInShipToAddress)
        : undefined,
    };
    return isTradeInItem ? tradeInItemParams : defaultParams;
  };

  const onViewResaleListing = async () => {
    // Open up a new (blank) tab immediately and then fill it with content later. Otherwise the
    // new tab opening could be considered a pop-up (e.g. non user triggered tab opening).
    const listingNewTab = window.open('', '_blank');
    listingNewTab?.document.write('Retrieving listing...');
    if (!resaleListing) return;
    const url = createListingURLHelper(
      routes,
      resaleListing.title,
      resaleListing.id,
      resaleListing.state
    );
    if (listingNewTab) {
      listingNewTab.location.href = url;
    }
  };

  const onCreateResaleListingV1 = async (payloadParams: BuildDraftListingPayload) => {
    const draftListingPayload = buildSTDraftListingPayloadFromExistingListing(payloadParams);
    const currentResaleListing = await dispatch(
      createSTListingDraftFromExistingListing(draftListingPayload)
    );

    setResaleListing(currentResaleListing);

    return { id: currentResaleListing?.id, title: currentResaleListing?.title };
  };

  const onCreateResaleListingV2 = async (payloadParams: BuildDraftListingPayload) => {
    const draftListingPayload = await buildPGDraftListingPayloadFromExistingListing(
      payloadParams,
      shopId
    );
    const currentResaleListing = await dispatch(
      createPGListingDraftFromExistingListing(draftListingPayload)
    );

    setResaleListing(currentResaleListing);

    return { id: currentResaleListing?.id, title: currentResaleListing?.title };
  };

  const onCreateResaleListing = async () => {
    // Open up a new (blank) tab immediately and then fill it with content later. Otherwise the
    // new tab opening could be considered a pop-up (e.g. non user triggered tab opening).
    const draftListingNewTab = window.open('', '_blank');
    draftListingNewTab?.document.write('Generating listing...');

    const payloadParams = getCreateListingDraftParams();
    const draftListingParams = isListingFlowV2
      ? await onCreateResaleListingV2(payloadParams)
      : await onCreateResaleListingV1(payloadParams);

    const { id, title } = draftListingParams;

    // Open the draft marketplace listing in a new tab
    const to = createResourceLocatorString(
      'EditListingPage',
      routes,
      {
        id,
        type: ListingPageParamType.Draft,
        slug: createSlug(title || ''),
        tab: ListingPageParamTab.Details,
      },
      {}
    );
    if (draftListingNewTab) {
      draftListingNewTab.location.href = to;
    }
  };

  const shouldUseDisabledClassName = isDisputed || isCanceled || isReturned;

  return (
    <Box
      p={isReturnSummaryPage ? 0 : 2}
      width={isReturnSummaryPage ? '100%' : 'auto'}
      className={classNames(css.itemizedListing, { [css.disabled]: shouldUseDisabledClassName })}
    >
      <Grid container alignItems="center">
        <Grid item xs={3}>
          <ConditionalWrapper
            condition={listingItemType === ListingItemType.Marketplace}
            wrapper={(children) => (
              <NamedLink
                name="ListingPage"
                onClick={() => logSelectItem(listing, referrerForLogging)}
                params={{ id: listing.id.uuid, slug: createSlug(listing.attributes.title) }}
              >
                {children}
              </NamedLink>
            )}
          >
            <ListingImage
              rootClassName={css.listingImage}
              currentListing={listing}
              title={listing.attributes.title}
            />
          </ConditionalWrapper>
        </Grid>
        <Grid item xs={6}>
          <Box display="flex" flexDirection="column" alignSelf="center" ml="8px">
            <ConditionalWrapper
              condition={isMarketplaceItem}
              wrapper={(children) => (
                <NamedLink
                  name="ListingPage"
                  onClick={() => logSelectItem(listing, referrerForLogging)}
                  params={{ id: listing.id.uuid, slug: createSlug(listing.attributes.title) }}
                >
                  {children}
                </NamedLink>
              )}
            >
              <TypographyWrapper
                variant="body1"
                typographyOverrides={{
                  style: {
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    fontWeight: 'bold',
                  },
                }}
                isDynamicValue
              >
                {listing.attributes.title}
              </TypographyWrapper>
            </ConditionalWrapper>
            {size && (
              <div>
                <TypographyWrapper
                  variant="body1"
                  typographyOverrides={{ display: 'inline' }}
                  isDynamicValue
                >
                  {'Size: '}
                </TypographyWrapper>
                <TypographyWrapper
                  variant="body1"
                  typographyOverrides={{ display: 'inline' }}
                  isDynamicValue
                >
                  {size}
                </TypographyWrapper>
              </div>
            )}
            {isBrand && shopifyProductVariantSku && (
              <TypographyWrapper variant="body1">
                SKU:{' '}
                <TypographyWrapper
                  variant="body1"
                  typographyOverrides={{ display: 'inline' }}
                  isDynamicValue
                >
                  {shopifyProductVariantSku}
                </TypographyWrapper>
              </TypographyWrapper>
            )}
            {!isBrand && isMarketplaceItem && isSales && !isCanceled && !isDisputed && (
              <CreditBoostChip listing={listing} />
            )}
            {isTradeInAdmin && conditionLabel && (
              <TypographyWrapper variant="body1">Condition: {conditionLabel}</TypographyWrapper>
            )}
            {isBrand && internalBrandNotes && (
              <TypographyWrapper variant="body1">
                <TypographyWrapper
                  variant="body1"
                  typographyOverrides={{ display: 'inline' }}
                  isDynamicValue
                >
                  {internalBrandNotes}
                </TypographyWrapper>
              </TypographyWrapper>
            )}
            {isTradeInItem && isVerified && (
              <TradeInCreditInfo
                hideDollarAmounts={shouldHideLineItemTotals}
                creditPayoutFixedAmount={creditPayoutFixedAmount}
                creditPayoutPercentage={creditPayoutPercentage}
                disputeDescription={disputeDescription}
              />
            )}

            {isDisputed && !isReturned && (
              <TypographyWrapper variant="body1">Disputed</TypographyWrapper>
            )}
            {isReturned && <TypographyWrapper variant="body1">Returned</TypographyWrapper>}
            {isCanceled && <TypographyWrapper variant="body1">Canceled</TypographyWrapper>}
            {canCreateResaleDraftListing && (
              <InlineTextButton
                onClick={resaleListing ? onViewResaleListing : onCreateResaleListing}
              >
                <Box display="flex" alignItems="center" justifyContent="center" mt={1}>
                  <IconRelistFilled className={css.icon} />
                  <Box
                    title={
                      resaleListing
                        ? 'View Listing Page for Relisted Item'
                        : 'List this item for sale'
                    }
                    style={{ textDecoration: 'underline', textUnderlineOffset: '2px' }}
                    ml="2px"
                  >
                    <TypographyWrapper variant="body1">
                      {viewCreatedListingActionCopy(resaleListing)}
                    </TypographyWrapper>
                  </Box>
                </Box>
              </InlineTextButton>
            )}
          </Box>
        </Grid>
        <Grid
          item
          xs={3}
          alignContent="flex-end"
          style={{ padding: isReturnSummaryPage ? 0 : 'auto' }}
        >
          <Box display="flex" flexDirection="row" justifyContent="flex-end">
            {!shouldHideLineItemTotals && lineItem?.listingLineItem && (
              <h4 className={css.listingPrice}>
                <TypographyWrapper variant={isReturnSummaryPage ? 'body2' : 'body1'} isDynamicValue>
                  {formatDollarAmountAsCurrency(
                    intl,
                    lineItem.listingLineItem.price,
                    lineItem.listingLineItem.currency
                  )}
                </TypographyWrapper>
              </h4>
            )}
            {actionEl}
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ItemizedListing;
